import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { AuthService } from 'src/app/Service/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private router: Router, private authService: AuthService) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const elementKeys: string[] = Array.isArray(route.data['element'])
      ? route.data['element']
      : [route.data['element']];
    const requiredPermissions: string[] = route.data['permissions'];

    const userDataString = localStorage.getItem('userData');
    if (!userDataString) {
      this.router.navigate(['/auth/login']);
      return of(false);
    }

    const userData = JSON.parse(userDataString);

    // 👉 derive role-scoped permission without mutating original userData
    const selectedRole = localStorage.getItem('selectedRole');
    const effectiveUserData = (() => {
      if (!selectedRole) return userData;

      const rolePerm = (userData?.RoleMenuPermissions || []).filter(
        (p: RolePermission) => p.role_key === selectedRole
      );

      if (rolePerm.length === 0) {
        return userData; // fallback to full userData if no matching role found
      }

      return {
        ...userData,
        Roles: [selectedRole],
        RoleMenuPermissions: rolePerm,
      };
    })();

    return new Observable<boolean>((observer) => {
      const rolePermissionsObservables: Observable<boolean>[] = [];

      for (const userRole of effectiveUserData.Roles) {
        const rolePermissionObservable = new Observable<boolean>((roleObserver) => {
          const rolePermissions: RolePermission[] = effectiveUserData.RoleMenuPermissions.filter(
            (permission: RolePermission) => permission.role_key === userRole
          );

          if (rolePermissions.length > 0) {
            const hasPermission = rolePermissions.some((permission: RolePermission) => {
              const matchesElement = elementKeys.some(
                (key) => permission.element_key.toUpperCase() === key.toUpperCase()
              );
              const matchesPermissions = requiredPermissions.every(
                (requiredPermission) =>
                  permission.hasOwnProperty(requiredPermission) &&
                  permission[requiredPermission] === true
              );
              return matchesElement && matchesPermissions;
            });

            roleObserver.next(hasPermission);
          } else {
            roleObserver.next(false);
          }

          roleObserver.complete();
        });

        rolePermissionsObservables.push(rolePermissionObservable);
      }

      forkJoin(rolePermissionsObservables).subscribe((results) => {
        const hasPermission = results.some((result) => result === true);

        if (hasPermission) {
          observer.next(true);
        } else {
          const redirectTo = route.data['redirectTo'] || '/unauthorized';
          this.router.navigate([redirectTo]);
          observer.next(false);
        }
        observer.complete();
      });
    });
  }
}

interface RolePermission {
  role_key: string;
  element_key: string;
  [key: string]: any;
}
